import moment from "moment";

import UtilityService from "./UtilityService";
import PlaybookRunService from "./PlaybookRunService";
import PlaybookScriptService from "./PlaybookScriptService";
import { authedFetch } from "./apiFetch";
import { postToAgents } from "./PlaybookApi";
import { BRIEF_FIELD_LABELS, PREVIEW_PARTS } from "../constants/PlaybookBriefs";

const USABLE_TEST_STATUSES = ["completed", "needs_review"];
const BRIEF_CATEGORY_LABEL = "Category";
const NOT_NEEDED_TEXT = "Not needed";
const BUILD_STAGE_LABELS = { describe: "Describe the job", build: "Build the draft", test: "Test it on a client", publish: "Publish" };
const BUILD_STAGE_PURPOSES = {
	describe: "Tell the guide what it does",
	build: "The guide writes it",
	test: "Run it on one client",
	publish: "Make it live for the firm",
};
const GAP_DECISIONS_CHANGED_REASON = "Build again: the draft was built with different gap decisions.";

const PlaybookBuildService = {
	/**
	 * Opens a build conversation: a new agent (optionally starting in a category, by its id), or a
	 * change to one the viewer owns.
	 *
	 * @param {Object} params { playbookId, categoryId }
	 * @return {Promise<Object>} the build session
	 */
	startBuild({ playbookId, categoryId }) {
		return postToAgents({ operationId: "startPlaybookBuild", body: { playbookId, categoryId } });
	},

	/**
	 * Saves which sources the agent being built reads, each required or helpful; resolves to the
	 * updated session.
	 *
	 * @param {Object} params { sessionId, sources: [{ key, isRequired }] }
	 * @return {Promise<Object>}
	 */
	setBuildSources({ sessionId, sources }) {
		return postToAgents({ operationId: "setPlaybookBuildSources", body: { sessionId, sources } });
	},

	/**
	 * Takes a skill out of the agent being built, or puts it back; resolves to the updated session.
	 *
	 * @param {Object} params { sessionId, skillId, isRemoved }
	 * @return {Promise<Object>}
	 */
	setBuildSkillRemoved({ sessionId, skillId, isRemoved }) {
		return postToAgents({ operationId: "setPlaybookBuildSkill", body: { sessionId, skillId, isRemoved } });
	},

	/**
	 * Records which firm skill was saved from a skill the guide drafted in the conversation; resolves to the updated session.
	 *
	 * @param {Object} params { sessionId, draftId, skillId }
	 * @return {Promise<Object>}
	 */
	linkDraftedSkill({ sessionId, draftId, skillId }) {
		return postToAgents({ operationId: "linkPlaybookBuildDraftedSkill", body: { sessionId, draftId, skillId } });
	},

	/**
	 * A build conversation with its brief, suggestions, draft and test runs.
	 *
	 * @param {Object} params { sessionId }
	 * @return {Promise<Object>}
	 */
	getBuild({ sessionId }) {
		return postToAgents({ operationId: "getPlaybookBuild", body: { sessionId } });
	},

	/**
	 * Sends a message to the agent development guide; resolves to the updated session.
	 *
	 * @param {Object} params { sessionId, text }
	 * @return {Promise<Object>}
	 */
	sendBuildMessage({ sessionId, text }) {
		return postToAgents({ operationId: "sendPlaybookBuildMessage", body: { sessionId, text } });
	},

	/**
	 * Starts building from a prompt written elsewhere; resolves to the updated session.
	 *
	 * @param {Object} params { sessionId, prompt }
	 * @return {Promise<Object>}
	 */
	importBuildPrompt({ sessionId, prompt }) {
		return postToAgents({ operationId: "importPlaybookBuildPrompt", body: { sessionId, prompt } });
	},

	/**
	 * Builds the draft from the brief; resolves to the updated session.
	 *
	 * @param {Object} params { sessionId }
	 * @return {Promise<Object>}
	 */
	buildDraft({ sessionId }) {
		return postToAgents({ operationId: "buildPlaybookDraft", body: { sessionId } });
	},

	/**
	 * Runs the draft on one client as a test. Resolves to { runId, session }.
	 *
	 * @param {Object} params { sessionId, clientId, inputs }
	 * @return {Promise<Object>}
	 */
	testDraft({ sessionId, clientId, inputs }) {
		return postToAgents({ operationId: "testPlaybookDraft", body: { sessionId, clientId, inputs } });
	},

	/**
	 * Publishes the tested draft; resolves to the updated session.
	 *
	 * @param {Object} params { sessionId }
	 * @return {Promise<Object>}
	 */
	publishDraft({ sessionId }) {
		return postToAgents({ operationId: "publishPlaybookDraft", body: { sessionId } });
	},

	/**
	 * Waives a statement line of the draft that no check is about, with the person's reason;
	 * resolves to the updated session.
	 *
	 * @param {Object} params { sessionId, canonicalCode, reason }
	 * @return {Promise<Object>}
	 */
	waiveDraftLine({ sessionId, canonicalCode, reason }) {
		return postToAgents({ operationId: "waivePlaybookDraftLine", body: { sessionId, canonicalCode, reason } });
	},

	/**
	 * Removes the waiver on a statement line of the draft; resolves to the updated session.
	 *
	 * @param {Object} params { sessionId, canonicalCode }
	 * @return {Promise<Object>}
	 */
	clearDraftLineWaiver({ sessionId, canonicalCode }) {
		return postToAgents({ operationId: "clearPlaybookDraftLineWaiver", body: { sessionId, canonicalCode } });
	},

	/**
	 * Builds without a gap the guide named (a step needing something no Workflow tool does), with
	 * the person's reason; resolves to the updated session.
	 *
	 * @param {Object} params { sessionId, gapId, reason }
	 * @return {Promise<Object>}
	 */
	waiveBuildGap({ sessionId, gapId, reason }) {
		return postToAgents({ operationId: "waivePlaybookBuildGap", body: { sessionId, gapId, reason } });
	},

	/**
	 * Drops the step a gap is about, so the guide leaves it out of the playbook; resolves to the
	 * updated session.
	 *
	 * @param {Object} params { sessionId, gapId }
	 * @return {Promise<Object>}
	 */
	dropBuildGap({ sessionId, gapId }) {
		return postToAgents({ operationId: "dropPlaybookBuildGap", body: { sessionId, gapId } });
	},

	/**
	 * Opens a waived or dropped gap again (removes the waiver, or brings the step back), so the
	 * draft cannot be built until it is resolved again; resolves to the updated session.
	 *
	 * @param {Object} params { sessionId, gapId }
	 * @return {Promise<Object>}
	 */
	reopenBuildGap({ sessionId, gapId }) {
		return postToAgents({ operationId: "reopenPlaybookBuildGap", body: { sessionId, gapId } });
	},

	/**
	 * A build conversation's gaps as the build page's lines: an open gap says what Workflow can't do
	 * yet and offers building without it or dropping the step; a waived one names who waived it,
	 * when and why, and offers removing the waiver; a dropped one says the step was dropped and
	 * offers bringing it back; a scripted one says a script covers it. Open gaps come first, in the
	 * order the guide named them.
	 *
	 * Script choices are offered only while scripts are switched on for the firm: an open gap offers
	 * writing a script, taking one from the firm's scripts (only when the firm has any), and, when it
	 * still holds a script taken off it, using that script again (listed first); a scripted gap offers
	 * taking its script off. With scripts off no script choice is offered at all.
	 *
	 * @param {Object} params
	 * @param {Array} params.gaps [{ id, step, whatItNeeds, status, reason, resolvedAt, resolvedByName }]
	 * @param {Boolean} [params.areScriptsOn] whether scripts are switched on for the firm; off by default
	 * @param {Boolean} [params.hasFirmScripts] whether the firm's scripts hold any entry; none by default
	 * @param {Array<String>} [params.gapIdsWithAKeptScript] ids of open gaps that still hold a script
	 *   taken off them; none by default
	 * @return {Object} { openCount, lines: [{ id, step, status, detail, choices }] } with choices from
	 *   "useScriptAgain", "writeScript", "useFirmScript", "buildWithout", "drop", "removeWaiver",
	 *   "bringBack", "removeScript"
	 */
	describeBuildGaps({ gaps, areScriptsOn = false, hasFirmScripts = false, gapIdsWithAKeptScript = [] }) {
		const choicesByStatus = {
			open: ["useScriptAgain", "writeScript", "useFirmScript", "buildWithout", "drop"],
			waived: ["removeWaiver"],
			dropped: ["bringBack"],
			scripted: ["removeScript"],
		};
		const scriptChoices = ["useScriptAgain", "writeScript", "useFirmScript", "removeScript"];
		const isChoiceOffered = ({ gap, choice }) => {
			const isScriptChoice = scriptChoices.includes(choice);
			if (isScriptChoice && !areScriptsOn) return false;
			if (choice === "useFirmScript") return hasFirmScripts;
			if (choice === "useScriptAgain") {
				const holdsAKeptScript = gapIdsWithAKeptScript.includes(gap.id);
				return holdsAKeptScript;
			}
			return true;
		};
		const describeChoices = gap => {
			const choicesForStatus = choicesByStatus[gap.status];
			const offeredChoices = choicesForStatus.filter(choice => isChoiceOffered({ gap, choice }));
			return offeredChoices;
		};
		const describeDetail = gap => {
			const resolvedOn = gap.resolvedAt ? moment(gap.resolvedAt).format("MMM D, YYYY") : "";
			const resolvedBy = gap.resolvedByName || "someone no longer listed";
			const detailByStatus = {
				open: `This needs something Workflow can't do yet: ${gap.whatItNeeds}`,
				waived: `Built without it — waived by ${resolvedBy} on ${resolvedOn}: ${gap.reason}`,
				dropped: `Step dropped by ${resolvedBy} on ${resolvedOn}. The guide leaves it out of the playbook.`,
				scripted: `A script covers this: ${gap.whatItNeeds}`,
			};
			const detail = detailByStatus[gap.status];
			return detail;
		};
		const describedLines = gaps.map(gap => ({
			id: gap.id,
			step: gap.step,
			status: gap.status,
			detail: describeDetail(gap),
			choices: describeChoices(gap),
		}));
		const openLines = describedLines.filter(line => line.status === "open");
		const resolvedLines = describedLines.filter(line => line.status !== "open");
		const lines = [...openLines, ...resolvedLines];
		return { openCount: openLines.length, lines };
	},

	/**
	 * Closes a build conversation without publishing.
	 *
	 * @param {Object} params { sessionId }
	 * @return {Promise<Object>}
	 */
	closeBuild({ sessionId }) {
		return postToAgents({ operationId: "closePlaybookBuild", body: { sessionId } });
	},

	/**
	 * The viewer's open build conversations.
	 *
	 * @return {Promise<Array>}
	 */
	listMyBuilds() {
		return postToAgents({ operationId: "listMyPlaybookBuilds", body: {} });
	},

	/**
	 * Removes a reference file from the agent being built; resolves to the updated session.
	 *
	 * @param {Object} params { sessionId, fileId }
	 * @return {Promise<Object>}
	 */
	removeReferenceFile({ sessionId, fileId }) {
		return postToAgents({ operationId: "removePlaybookReferenceFile", body: { sessionId, fileId } });
	},

	/**
	 * Uploads reference files for the agent being built (multipart, so not through Kichiri).
	 * Resolves to the updated session, or throws with the server's reason.
	 *
	 * @param {Object} params
	 * @param {Number} params.sessionId
	 * @param {Array<File>} params.files
	 * @return {Promise<Object>}
	 */
	async uploadReferenceFiles({ sessionId, files }) {
		const formData = new FormData();
		formData.append("sessionId", String(sessionId));
		files.forEach(file => formData.append("files", file));
		const response = await authedFetch("/playbooks/build/files/upload", { method: "POST", body: formData });
		const body = await response.json();
		if (!response.ok) throw new Error(String(body.error || "The file was not added.").replace(/^Error: /, ""));
		return body;
	},

	/**
	 * Plain labels for the brief's fields, in the order the build screen shows them.
	 *
	 * @return {Array} [{ key, label }]
	 */
	briefFields() {
		return BRIEF_FIELD_LABELS;
	},

	/**
	 * Whether a brief field was settled as not applying to this one: the brief lists it in
	 * notNeededFields, so the build screen shows it quietly. It still counts as settled.
	 *
	 * @param {Object} params
	 * @param {Object} params.brief
	 * @param {String} params.fieldKey
	 * @return {Boolean}
	 */
	isBriefFieldNotNeeded({ brief, fieldKey }) {
		const isNotNeeded = brief.notNeededFields.includes(fieldKey);
		return isNotNeeded;
	},

	/**
	 * Whether a brief field is settled: answered with text, or listed as not needed.
	 *
	 * @param {Object} params
	 * @param {Object} params.brief
	 * @param {String} params.fieldKey
	 * @return {Boolean}
	 */
	isBriefFieldSettled({ brief, fieldKey }) {
		const isAnswered = brief[fieldKey] !== "";
		const isSettled = isAnswered || this.isBriefFieldNotNeeded({ brief, fieldKey });
		return isSettled;
	},

	/**
	 * Whether the sources chosen in a build conversation differ from the ones the draft was built
	 * with, which and how much each is needed both counting — so the draft must be rebuilt to use them.
	 *
	 * @param {Object} params
	 * @param {Object} params.session
	 * @return {Boolean}
	 */
	isDraftBehindOnSources({ session }) {
		if (!session.draft) return false;
		const describeNeed = source => `${source.key}:${source.isRequired}`;
		const draftNeeds = session.draft.sources.map(describeNeed).sort();
		const chosenNeeds = session.sources.map(describeNeed).sort();
		const isBehind = draftNeeds.join(",") !== chosenNeeds.join(",");
		return isBehind;
	},

	/**
	 * Whether a skill the draft was built with has since been taken out of the build conversation,
	 * so the draft must be rebuilt to drop it.
	 *
	 * @param {Object} params
	 * @param {Object} params.session
	 * @return {Boolean}
	 */
	isDraftBehindOnSkills({ session }) {
		if (!session.draft) return false;
		const removedSkillIds = session.removedSkills.map(skill => skill.id);
		const isBehind = session.draft.skills.some(skill => removedSkillIds.includes(skill.id));
		return isBehind;
	},

	/**
	 * The skills and tools a build conversation shows: the draft's skills once a draft exists, else
	 * the ones the guide suggested, without any skill the person took out (session.removedSkills,
	 * matched by id); the draft's tools once a draft exists, else the suggested ones. The Skills
	 * card and the playbook preview both read it, so they always list the same ones.
	 *
	 * @param {Object} params
	 * @param {Object} params.session the build session as getPlaybookBuild returns it
	 * @return {Object} { skills: [{ id, name, versionNumber, reason }], tools: [{ name, label, needs }] }
	 */
	skillsAndToolsShown({ session }) {
		const { draft } = session;
		const removedSkillIds = session.removedSkills.map(skill => skill.id);
		const skillsListed = draft ? draft.skills : session.suggestedSkills;
		const skills = skillsListed.filter(skill => !removedSkillIds.includes(skill.id));
		const tools = draft ? draft.tools : session.suggestedTools;
		return { skills, tools };
	},

	/**
	 * Why Build is off, as the one sentence the page shows for it, with the reason it stands for;
	 * null when no such sentence shows. Moved unchanged in meaning out of the Draft card's render, so
	 * the card, the header's next step and the to-do list all say it one way. It shows only while the
	 * conversation is open and the server says the brief is not ready to build (isReadyToBuild), and
	 * then only with no draft yet, a gap open, a gap waived, dropped or scripted after the guide's
	 * last message (the guide has not yet rewritten the brief around that decision; with no guide
	 * message yet, every such gap counts), or the server asking for the draft to be built again for a
	 * reason other than an unapproved script step (the build stage's server rebuildText: every build
	 * sets isReadyToBuild false, playbookBuildDraftService.js:477, and only the guide's next reply sets
	 * it back, playbookBuildGuideService.js:427). A rebuild asked only for draft.scriptStepsBlock shows
	 * no sentence: approving the script sets isReadyToBuild with no reply from the guide
	 * (playbookScriptApprovalService.js:180-183, playbookBuildGapRules.js:221-226), and the script
	 * to-dos and the rebuild item already say what to do. An open gap wins ("openGaps"); then a gap
	 * resolved since the guide answered ("waitingOnGuide", worded for a script when the latest of those
	 * gaps is scripted, else for a step left out); then the server's rebuild waiting on the guide's
	 * reply ("waitingOnGuideToRebuild", whose stage is build), only while no guide message is newer
	 * than draft.builtAt (once the guide has replied, each reply sets isReadyToBuild from isBriefReady,
	 * so a brief still not ready is unanswered, not waiting); otherwise the brief still has answers to
	 * give ("briefUnanswered"). It is worked out in describeBuildStages, beside the rebuild it may wait on,
	 * and read from the build stage here, so the condition is computed once.
	 *
	 * @param {Object} params
	 * @param {Object} params.session the build session as getPlaybookBuild returns it
	 * @return {Object|null} { text, reason } with reason "openGaps" | "waitingOnGuide" | "waitingOnGuideToRebuild" | "briefUnanswered"
	 */
	whyBuildIsOff({ session }) {
		const stages = this.describeBuildStages({ session });
		const buildStage = stages.find(stage => stage.key === "build");
		const whyOff = buildStage.whyBuildIsOff;
		return whyOff;
	},

	/**
	 * Where a playbook build stands, as four stages read only from what the server already decided:
	 * describe the job, build the draft, test it on a client, publish. Each is "done", "current" or
	 * "upcoming"; the first stage not done is current while the conversation is open, and a
	 * conversation that is not open has no current stage (a published one has every stage done).
	 *
	 * - describe is done when a draft exists or the server says the brief is ready to build, no gap
	 *   is open, and whyBuildIsOff has no sentence but its rebuild one (a gap resolved since the guide last answered means
	 *   the brief still waits on the guide, even with a draft). Its detail is how many steps are left
	 *   to settle while a gap is open, "Brief complete" once the stage is done, else null, so the
	 *   tracker shows the stage's purpose and never an answered count (the playbook preview's rings
	 *   carry progress). stillToAnswer lists the labels not yet settled (isBriefFieldSettled over
	 *   briefFields), the category last; isBriefUntouched says no brief field is settled yet, the
	 *   category not counted (a build started from a category has it from the start), so the to-do
	 *   list words a cold start from this one count.
	 * - every stage carries its purpose (BUILD_STAGE_PURPOSES), the plain line the tracker shows
	 *   under a stage with no detail.
	 * - build is done when a draft exists, no rebuild is owed for the person's changes, and the
	 *   server's whyNotPublish does not ask for the draft to be built again. A rebuild is owed for
	 *   changes only while the server says the brief is ready to build and the draft is behind: on
	 *   sources, on skills, or a step dropped after it was built (the server's whyGapsStopPublishing
	 *   counts only drops after the build, api services/playbookBuildGapRules.js:116-118; a gap
	 *   re-waived or scripted after the build refreshes the draft's own record and may publish, and
	 *   when the draft's gap decisions really differ the server says so itself). A ready brief with a
	 *   draft that is not behind is not owed a rebuild (the server sets isReadyToBuild again after any
	 *   reply once the brief is complete, playbookBuildGuideService.js:394), and a draft that is
	 *   behind while the brief is not ready cannot be built (the server refuses,
	 *   playbookBuildDraftService.js:241). The server asks to build again (api
	 *   services/playbookBuildService.js:418-426, whose order this follows) when, with scripts not
	 *   switched off (draft.scriptsBlock, which refuses the test instead), a script
	 *   step's script is missing or was not approved when the draft was built (draft.scriptStepsBlock:
	 *   the version's pins never move, playbookScriptApprovalService.js:158-159, so approving the
	 *   script still needs a rebuild), the draft's judgments were not mapped (draft.decisionPoints
	 *   empty), the facts they record were not planned or need a missing tool (draft.tracking), or
	 *   its gap decisions differ (that sentence alone has no field of its own, so it is matched as
	 *   GAP_DECISIONS_CHANGED_REASON, playbookBuildGapRules.js:120). The build stage's rebuildText is
	 *   what the to-do list asks: "Build again to use your changes" for the person's changes, else
	 *   the server's own sentence verbatim, else null; a gap open does not hide it (the server asks it ahead of its gaps sentence, so a test of this draft could never count),
	 *   only the to-do list does. The build stage also carries whyBuildIsOff (see that method), worked out here as its rebuild case needs the rebuild.
	 * - test is done only when a test run of the current draft is usable: status "completed" or
	 *   "needs_review", the web's mirror of the server's isTestRunUsable (api
	 *   services/playbookBuildService.js:49-52), which is what canPublish counts. A failed,
	 *   cancelled, interrupted, skipped or needs_linking run does not count, though
	 *   PlaybookRunService.isFinished calls it finished. Its detail names the client of the usable
	 *   test, or says a test of the current draft is running.
	 * - publish is done when the conversation was published.
	 *
	 * @param {Object} params
	 * @param {Object} params.session the build session as getPlaybookBuild returns it
	 * @return {Array} [{ key: "describe" | "build" | "test" | "publish", label, purpose, state, detail, stillToAnswer, isBriefUntouched, rebuildText, whyBuildIsOff }]
	 */
	describeBuildStages({ session }) {
		const { draft } = session;
		const isOpen = session.status === "open";
		const isPublished = session.status === "published";
		const openGaps = session.gaps.filter(gap => gap.status === "open");
		const hasOpenGaps = openGaps.length > 0;

		const whyDraftCannotPublish = draft && !session.canPublish ? session.whyNotPublish : null;
		const areScriptsSwitchedOff = !!draft && !!draft.scriptsBlock;
		const isScriptStepUnapprovedInDraft = !!draft && !!draft.scriptStepsBlock;
		const tracking = draft ? draft.tracking : null;
		const isTrackingRebuildAsked = !!tracking && (!tracking.isPlanned || tracking.gaps.length > 0);
		const areDecisionsUnmapped = !!draft && !draft.decisionPoints;
		const areGapDecisionsChanged = whyDraftCannotPublish === GAP_DECISIONS_CHANGED_REASON;
		const isRebuildReason = isScriptStepUnapprovedInDraft || areDecisionsUnmapped || isTrackingRebuildAsked || areGapDecisionsChanged;
		const isServerAskingRebuild = !!whyDraftCannotPublish && !areScriptsSwitchedOff && isRebuildReason;
		const serverRebuildText = isServerAskingRebuild ? whyDraftCannotPublish : null;

		const timeOf = ({ at }) => new Date(at).getTime();
		const guideMessages = session.messages.filter(message => message.from === "guide");
		const guideMessageTimes = guideMessages.map(message => timeOf({ at: message.at }));
		const lastGuideMessageTime = guideMessageTimes.length > 0 ? Math.max(...guideMessageTimes) : null;
		const resolvedStatuses = ["waived", "dropped", "scripted"];
		const resolvedGaps = session.gaps.filter(gap => resolvedStatuses.includes(gap.status));
		const gapsResolvedSinceGuideAnswered = resolvedGaps.filter(gap => lastGuideMessageTime === null || timeOf({ at: gap.resolvedAt }) > lastGuideMessageTime);
		const isGapResolvedSinceGuideAnswered = gapsResolvedSinceGuideAnswered.length > 0;
		const laterResolvedGap = (latest, gap) => (latest === null || timeOf({ at: gap.resolvedAt }) > timeOf({ at: latest.resolvedAt }) ? gap : latest);
		const latestResolvedGap = gapsResolvedSinceGuideAnswered.reduce(laterResolvedGap, null);
		const isLatestResolvedGapScripted = latestResolvedGap !== null && latestResolvedGap.status === "scripted";
		const builtTime = draft ? new Date(draft.builtAt).getTime() : null;
		const guideReplies = guideMessages.filter(message => message.kind !== "built");
		const guideReplyTimes = guideReplies.map(message => timeOf({ at: message.at }));
		const isGuideRepliedSinceBuild = !!draft && guideReplyTimes.some(replyTime => replyTime > builtTime);
		const isRebuildAskedBeyondScripts = isServerAskingRebuild && (areDecisionsUnmapped || isTrackingRebuildAsked || areGapDecisionsChanged);
		const isRebuildWaitingOnGuide = isRebuildAskedBeyondScripts && !isGuideRepliedSinceBuild;
		const isWhyOffShown = isOpen && !session.isReadyToBuild && (!draft || hasOpenGaps || isGapResolvedSinceGuideAnswered || isRebuildAskedBeyondScripts);
		const waitingOnGuideWithoutStepText = "Send the guide a message so it rewrites the brief without that step; Build turns on once it has.";
		const waitingOnGuideForScriptText = "Send the guide a message so it updates the brief to use the script's results; Build turns on once it has.";
		const waitingOnGuideText = isLatestResolvedGapScripted ? waitingOnGuideForScriptText : waitingOnGuideWithoutStepText;
		const textByReason = {
			openGaps: "Build turns on once every step on the To do tab is settled: build without it, giving a reason, or drop the step.",
			waitingOnGuide: waitingOnGuideText,
			waitingOnGuideToRebuild: "Send the guide a message so it can update the brief; Build turns on once it has replied.",
			briefUnanswered: "Keep answering the guide. Build turns on once every part of the brief is answered.",
		};
		let whyOffReason = "briefUnanswered";
		if (isRebuildWaitingOnGuide) whyOffReason = "waitingOnGuideToRebuild";
		if (isGapResolvedSinceGuideAnswered) whyOffReason = "waitingOnGuide";
		if (hasOpenGaps) whyOffReason = "openGaps";
		const whyOff = isWhyOffShown ? { text: textByReason[whyOffReason], reason: whyOffReason } : null;
		const isBriefWaitingOnGuide = whyOff !== null && whyOff.reason !== "waitingOnGuideToRebuild";

		const fields = this.briefFields();
		const unansweredFields = fields.filter(field => !this.isBriefFieldSettled({ brief: session.brief, fieldKey: field.key }));
		const unansweredFieldLabels = unansweredFields.map(field => field.label);
		const unansweredCategoryLabels = session.category ? [] : [BRIEF_CATEGORY_LABEL];
		const stillToAnswer = [...unansweredFieldLabels, ...unansweredCategoryLabels];
		const isBriefUntouched = unansweredFields.length === fields.length;
		const isDescribed = (!!draft || session.isReadyToBuild) && !hasOpenGaps && !isBriefWaitingOnGuide;
		const isDescribeDone = isPublished || isDescribed;
		const stepsToSettleText = `${UtilityService.describeCount({ count: openGaps.length, singular: "step" })} to settle`;
		let describeDetail = null;
		if (isDescribeDone) describeDetail = "Brief complete";
		if (hasOpenGaps) describeDetail = stepsToSettleText;

		const isStepDroppedAfterBuild = !!draft && session.gaps.some(gap => gap.status === "dropped" && new Date(gap.resolvedAt).getTime() > builtTime);
		const isDraftBehind = this.isDraftBehindOnSources({ session }) || this.isDraftBehindOnSkills({ session }) || isStepDroppedAfterBuild;
		const isRebuildOwed = session.isReadyToBuild && isDraftBehind;
		const changesRebuildText = isRebuildOwed ? "Build again to use your changes" : null;
		const rebuildText = changesRebuildText || serverRebuildText;
		const isBuilt = !!draft && rebuildText === null;
		const buildDetail = draft ? `Draft ${draft.versionNumber}` : null;

		const currentDraftRuns = session.testRuns.filter(run => run.isOfCurrentDraft);
		const usableRun = currentDraftRuns.find(run => USABLE_TEST_STATUSES.includes(run.status));
		const runningRun = currentDraftRuns.find(run => !PlaybookRunService.isFinished({ status: run.status }));
		const isTested = !!usableRun;
		let testDetail = null;
		if (runningRun) testDetail = `Test on ${runningRun.client.name} is running`;
		if (usableRun) testDetail = usableRun.client.name;

		const isDoneByKey = {
			describe: isDescribeDone,
			build: isPublished || isBuilt,
			test: isPublished || isTested,
			publish: isPublished,
		};
		const detailByKey = { describe: describeDetail, build: buildDetail, test: testDetail, publish: null };
		const stageKeys = Object.keys(BUILD_STAGE_LABELS);
		const firstNotDoneKey = stageKeys.find(key => !isDoneByKey[key]);
		const stateOf = key => {
			if (isDoneByKey[key]) return "done";
			const isCurrent = isOpen && key === firstNotDoneKey;
			const state = isCurrent ? "current" : "upcoming";
			return state;
		};
		const stages = stageKeys.map(key => ({
			key,
			label: BUILD_STAGE_LABELS[key],
			purpose: BUILD_STAGE_PURPOSES[key],
			state: stateOf(key),
			detail: detailByKey[key],
			stillToAnswer: key === "describe" ? stillToAnswer : [],
			isBriefUntouched: key === "describe" ? isBriefUntouched : false,
			rebuildText: key === "build" ? rebuildText : null,
			whyBuildIsOff: key === "build" ? whyOff : null,
		}));
		return stages;
	},

	/**
	 * Everything in an open build conversation that waits on the person, in the order they should
	 * do it, each saying where it lives: a tab of the build page ("todo", "draft", "sources") or an
	 * action ("build", "publish", "reply"). Blocking items (on the path to publishing) come first,
	 * each only when it applies: open gaps; each script on its step not yet approved (scripts shown
	 * only once they have loaded and are switched on for the firm, so null scripts or viewer give
	 * none), naming its step through script.gapId, with the first thing the script itself still
	 * needs (describeScriptCard's approveBlockers without the viewer's own whyNotApprove, which is
	 * who may approve, not what the script lacks), else approving it when the viewer may approve
	 * (viewer.canApprove), else asking an approver; whyBuildIsOff's sentence when no gap is open
	 * (for an unanswered brief, one short line and never a field list: "Describe the job you'd like to automate" while no brief
	 * field is settled, from describeBuildStages' isBriefUntouched, else a nudge to keep answering that points at the Playbook
	 * tab while its stillToAnswer names something, and a bare "Keep answering the guide" when it names nothing; a rebuild waiting on the guide's reply stands in the build stage); Build the
	 * draft when the server says the brief is ready and there is none; building again in the build
	 * stage's rebuildText words (the person's changes, or the server's own build-again sentence; while a gap is open neither it nor a test is listed),
	 * with the Build action only while the brief is ready, else pointing at the Draft tab; the
	 * server's reason it refuses to test this draft (draft.scriptsBlock, then draft.scriptStepsBlock,
	 * the two testDraft refuses on, api services/playbookBuildPublishService.js:33-34), verbatim unless the rebuild already says it,
	 * and then no test is asked; a test of the current draft running, or none usable yet; unchecked,
	 * unwaived statement lines; then Publish when the server allows it, else its whyNotPublish
	 * verbatim, but only when no earlier item already says it (a Draft-tab item, which carries the
	 * test and coverage reasons, a rebuild, or the refused test). Optional items follow, never the next step: a skill the guide drafted in
	 * its latest message and nobody added, building again when the brief is ready and the draft is
	 * not behind, suggested sources not yet added, and each run input the latest build left out
	 * (session.fieldGaps, D14: a note on the Draft tab's Run inputs card, never a blocking gap). Each item carries its stage, the stage
	 * describeBuildStages marks current when that item is the next step; a script waiting for
	 * approval can stand at any point of the build, so it carries the current stage. A conversation
	 * that is not open has no to-dos.
	 *
	 * @param {Object} params
	 * @param {Object} params.session the build session as getPlaybookBuild returns it
	 * @param {Array|null} params.scripts listBuildScripts' scripts; null while they load
	 * @param {Object|null} params.viewer listBuildScripts' viewer { canApprove, areScriptsOn, whyNotApprove }; null while it loads
	 * @return {Array} [{ key, text, tab, action, stage, isOptional }] with tab "todo" | "draft" | "sources" | null and action "build" | "publish" | "reply" | null
	 */
	describeBuildTodos({ session, scripts, viewer }) {
		if (session.status !== "open") return [];
		const { draft } = session;
		const loadedScripts = scripts || [];
		const areScriptsShown = !!viewer && viewer.areScriptsOn;
		const stages = this.describeBuildStages({ session });
		const stageNamed = key => stages.find(stage => stage.key === key);
		const currentStage = stages.find(stage => stage.state === "current");
		const isBuildStageDone = stageNamed("build").state === "done";
		const isTestStageDone = stageNamed("test").state === "done";
		const todo = ({ key, text, tab = null, action = null, stage, isOptional = false }) => ({ key, text, tab, action, stage, isOptional });

		const openGaps = session.gaps.filter(gap => gap.status === "open");
		const hasOpenGaps = openGaps.length > 0;
		const openGapsText = `Settle ${UtilityService.describeCount({ count: openGaps.length, singular: "step" })} Workflow can't do yet`;
		const openGapItems = hasOpenGaps ? [todo({ key: "openGaps", text: openGapsText, tab: "todo", stage: "describe" })] : [];

		const waitingScripts = areScriptsShown ? loadedScripts.filter(script => script.isOnItsGap && !script.isApproved) : [];
		const scriptsWithTheirStep = waitingScripts.map(script => ({ script, gap: session.gaps.find(gap => String(gap.id) === String(script.gapId)) }));
		const scriptsOnAKnownStep = scriptsWithTheirStep.filter(({ gap }) => !!gap);
		const describeScriptTodo = ({ script, gap }) => {
			const card = PlaybookScriptService.describeScriptCard({ script, viewer });
			const scriptBlockers = card.approveBlockers.filter(blocker => blocker !== viewer.whyNotApprove);
			let text = `Ask an approver to approve the script for ${gap.step}`;
			if (viewer.canApprove) text = `Approve the script for ${gap.step}`;
			if (scriptBlockers.length > 0) text = `Finish the script for ${gap.step}: ${scriptBlockers[0]}`;
			const scriptTodo = todo({ key: `script-${script.pinId}`, text, tab: "todo", stage: currentStage.key });
			return scriptTodo;
		};
		const scriptItems = scriptsOnAKnownStep.map(describeScriptTodo);

		const whyOff = stageNamed("build").whyBuildIsOff;
		const { isBriefUntouched, stillToAnswer } = stageNamed("describe");
		const isAnythingLeftToAnswer = stillToAnswer.length > 0;
		let briefUnansweredText = "Keep answering the guide";
		if (isAnythingLeftToAnswer) briefUnansweredText = "Keep answering the guide; the Playbook tab shows what's still open";
		if (isBriefUntouched) briefUnansweredText = "Describe the job you'd like to automate";
		const isBriefUnanswered = whyOff !== null && whyOff.reason === "briefUnanswered";
		const whyOffText = isBriefUnanswered ? briefUnansweredText : whyOff && whyOff.text;
		const isWhyOffShown = whyOff !== null && !hasOpenGaps;
		const whyOffStage = isWhyOffShown && whyOff.reason === "waitingOnGuideToRebuild" ? "build" : "describe";
		const whyOffItems = isWhyOffShown ? [todo({ key: "whyBuildIsOff", text: whyOffText, action: "reply", stage: whyOffStage })] : [];

		const isFirstBuildOffered = session.isReadyToBuild && !draft;
		const { rebuildText } = stageNamed("build");
		const isRebuildAsked = !!draft && rebuildText !== null;
		const isRebuildHiddenByGaps = isRebuildAsked && hasOpenGaps;
		const isRebuildListed = isRebuildAsked && !isRebuildHiddenByGaps;
		const isOptionalRebuildOffered = session.isReadyToBuild && !!draft && isBuildStageDone;
		const firstBuildItems = isFirstBuildOffered ? [todo({ key: "build", text: "Build the draft", action: "build", stage: "build" })] : [];
		const rebuildAction = session.isReadyToBuild ? "build" : null;
		const rebuildTab = session.isReadyToBuild ? null : "draft";
		const rebuildItems = isRebuildListed ? [todo({ key: "build", text: rebuildText, tab: rebuildTab, action: rebuildAction, stage: "build" })] : [];

		const whyTestIsRefused = draft ? draft.scriptsBlock || draft.scriptStepsBlock || null : null;
		const refusedTestStage = isTestStageDone ? "publish" : "test";
		const isRefusalSaidByRebuild = isRebuildListed && whyTestIsRefused === rebuildText;
		const isRefusalShown = !!whyTestIsRefused && !isRefusalSaidByRebuild;
		const refusedTestItems = isRefusalShown ? [todo({ key: "whyNotTest", text: whyTestIsRefused, tab: "draft", stage: refusedTestStage })] : [];

		const runningTest = draft ? session.testRuns.find(run => run.isOfCurrentDraft && !PlaybookRunService.isFinished({ status: run.status })) : null;
		const isTestOwed = !!draft && !isTestStageDone;
		const isTestRunningShown = isTestOwed && !!runningTest;
		const isTestAsked = isTestOwed && !runningTest && !whyTestIsRefused && !isRebuildHiddenByGaps;
		const runningTestItems = isTestRunningShown ? [todo({ key: "testRunning", text: `Your test on ${runningTest.client.name} is running`, stage: "test" })] : [];
		const testItems = isTestAsked ? [todo({ key: "test", text: `Test draft ${draft.versionNumber} on a client`, tab: "draft", stage: "test" })] : [];

		const draftLines = draft ? this.describeDraftLines({ lineCoverage: draft.lineCoverage }).lines : [];
		const uncheckedLines = draftLines.filter(line => line.isUnchecked);
		const uncheckedLinesText = `Cover or waive ${UtilityService.describeCount({ count: uncheckedLines.length, singular: "statement line" })}`;
		const uncheckedLineItems = uncheckedLines.length > 0 ? [todo({ key: "uncheckedLines", text: uncheckedLinesText, tab: "draft", stage: "publish" })] : [];

		const earlierDraftTabItems = [...runningTestItems, ...testItems, ...uncheckedLineItems];
		const isWhyNotPublishSaidEarlier = earlierDraftTabItems.length > 0 || isRebuildAsked || !!whyTestIsRefused;
		const isWhyNotPublishShown = !session.canPublish && !!draft && !!session.whyNotPublish && !isWhyNotPublishSaidEarlier;
		const publishItems = session.canPublish ? [todo({ key: "publish", text: `Publish version ${draft.versionNumber}`, action: "publish", stage: "publish" })] : [];
		const whyNotPublishItems = isWhyNotPublishShown ? [todo({ key: "whyNotPublish", text: session.whyNotPublish, tab: "draft", stage: "publish" })] : [];

		const latestMessage = session.messages.length > 0 ? session.messages[session.messages.length - 1] : null;
		const isDraftedSkillWaiting = !!latestMessage && !!latestMessage.draftSkill && !latestMessage.draftSkill.addedSkill;
		const draftedSkillText = "Add the skill the guide drafted, or say not now";
		const draftedSkillItems = isDraftedSkillWaiting ? [todo({ key: "draftedSkill", text: draftedSkillText, action: "reply", stage: "describe", isOptional: true })] : [];
		const optionalRebuildText = "You can build again with your latest answers";
		const optionalRebuildItems = isOptionalRebuildOffered ? [todo({ key: "buildAgain", text: optionalRebuildText, action: "build", stage: "build", isOptional: true })] : [];
		const suggestedSourcesText = `Add or skip ${UtilityService.describeCount({ count: session.suggestedSources.length, singular: "source" })} the guide suggested`;
		const hasSuggestedSources = session.suggestedSources.length > 0;
		const suggestedSourceItems = hasSuggestedSources ? [todo({ key: "suggestedSources", text: suggestedSourcesText, tab: "sources", stage: "describe", isOptional: true })] : [];
		const fieldGaps = session.fieldGaps || [];
		const describeFieldGapTodo = (fieldGap, index) => {
			const quotedLabel = `"${fieldGap.label}"`;
			const isAlreadyNamed = !fieldGap.label || fieldGap.sentence.startsWith(quotedLabel);
			const text = isAlreadyNamed ? `A run input was left out: ${fieldGap.sentence}` : `Run input ${quotedLabel} was left out: ${fieldGap.sentence}`;
			const fieldGapTodo = todo({ key: `fieldGap-${index}`, text, tab: "draft", stage: "build", isOptional: true });
			return fieldGapTodo;
		};
		const fieldGapItems = fieldGaps.map(describeFieldGapTodo);

		const todos = [
			...openGapItems,
			...scriptItems,
			...whyOffItems,
			...firstBuildItems,
			...rebuildItems,
			...refusedTestItems,
			...earlierDraftTabItems,
			...publishItems,
			...whyNotPublishItems,
			...draftedSkillItems,
			...optionalRebuildItems,
			...suggestedSourceItems,
			...fieldGapItems,
		];
		return todos;
	},

	/**
	 * The build page's next step, the one sentence the header shows under the title: the first
	 * blocking item of describeBuildTodos (so the header, the stage tracker and the to-do list never
	 * disagree), and with none, where the conversation stands: published, closed, or open with
	 * nothing waiting but the conversation itself. Optional to-dos are never the next step.
	 *
	 * @param {Object} params
	 * @param {Object} params.session the build session as getPlaybookBuild returns it
	 * @param {Array|null} params.scripts listBuildScripts' scripts; null while they load
	 * @param {Object|null} params.viewer listBuildScripts' viewer; null while it loads
	 * @return {Object} { key, text, tab, action } as describeBuildTodos words an item, key being that item's own key (so the header and the to-do list name the same line), null when no item is waiting
	 */
	describeNextStep({ session, scripts, viewer }) {
		const todos = this.describeBuildTodos({ session, scripts, viewer });
		const blockingTodos = todos.filter(todo => !todo.isOptional);
		if (blockingTodos.length > 0) {
			const { key, text, tab, action } = blockingTodos[0];
			const nextStep = { key, text, tab, action };
			return nextStep;
		}
		if (session.status === "published") return { key: null, text: "Published. It is live for the firm.", tab: null, action: null };
		if (session.status !== "open") return { key: null, text: "This conversation is closed.", tab: null, action: null };
		return { key: null, text: "Keep answering the guide.", tab: null, action: "reply" };
	},

	/**
	 * The playbook as it is taking shape, for the build page's Playbook tab, read only from the
	 * session: its name and one-line description, the parts of the job (PREVIEW_PARTS, which give
	 * every brief field exactly one home), and a few details. Nothing here claims more than the
	 * brief says.
	 *
	 * - name and description are the draft's once a draft exists, else the brief's, else null; a
	 *   brief field marked not needed counts as empty here.
	 * - a part is "done" when every one of its fields is settled (isBriefFieldSettled), "partly"
	 *   when some are, "waiting" when none are. The skills part has no fields: it is "done" when any
	 *   skill or tool is listed, else "waiting", never "partly".
	 * - a part's lines are its settled fields in field order, each { label, text, isNotNeeded,
	 *   isLabelShown }: a field marked not needed shows "Not needed" and still counts as settled, as
	 *   BuildBrief shows it. isLabelShown is true when the part has more than one field and the
	 *   field is not in the part's unlabelledFieldKeys (the field whose label is the part's own
	 *   name), so no line label repeats its part.
	 * - extras: for the reads part, the chosen sources' labels (session.sources through
	 *   sourceCatalogue), then the documents' file names (session.referenceFiles); for the skills
	 *   part, the skill names, then the tool labels, picked by skillsAndToolsShown (the pick the
	 *   Skills card renders, PlaybookBuildSkillsCard.render); every other part, none.
	 * - details: Category (its name, "· new" for a category the guide proposed, "Not set yet"
	 *   without one), Draft ("Draft N", or "Not built yet"), Tested on (the test stage's detail,
	 *   the usable test's client, once that stage is done, else "Not yet").
	 * - isFilling: some part is not done, the brief's name or description is unsettled, or the
	 *   category is unset; the page shows its "fills in as you talk" line while it is true.
	 *
	 * @param {Object} params
	 * @param {Object} params.session the build session as getPlaybookBuild returns it
	 * @return {Object} { name, description, parts: [{ key, label, state, lines: [{ label, text, isNotNeeded, isLabelShown }], extras, emptyText }], details: [{ key, label, value }], isFilling }
	 */
	describePlaybookPreview({ session }) {
		const { brief, draft, category } = session;
		const isBriefNameEmpty = brief.name === "" || this.isBriefFieldNotNeeded({ brief, fieldKey: "name" });
		const isBriefDescriptionEmpty = brief.description === "" || this.isBriefFieldNotNeeded({ brief, fieldKey: "description" });
		const briefName = isBriefNameEmpty ? null : brief.name;
		const briefDescription = isBriefDescriptionEmpty ? null : brief.description;
		const name = draft ? draft.name || null : briefName;
		const description = draft ? draft.description || null : briefDescription;

		const fieldLabelByKey = Object.fromEntries(BRIEF_FIELD_LABELS.map(field => [field.key, field.label]));
		const { skills, tools } = this.skillsAndToolsShown({ session });
		const skillNames = skills.map(skill => skill.name);
		const toolLabels = tools.map(tool => tool.label);
		const chosenSources = session.sources.map(chosen => session.sourceCatalogue.find(source => source.key === chosen.key));
		const sourceLabels = chosenSources.map(source => source.label);
		const documentNames = session.referenceFiles.map(file => file.fileName);
		const extrasByPartKey = { reads: [...sourceLabels, ...documentNames], skills: [...skillNames, ...toolLabels] };
		const hasSkillsOrTools = extrasByPartKey.skills.length > 0;

		const headPart = PREVIEW_PARTS.find(part => part.key === "head");
		const jobParts = PREVIEW_PARTS.filter(part => part.key !== "head");
		const parts = jobParts.map(part => {
			const settledKeys = part.fieldKeys.filter(fieldKey => this.isBriefFieldSettled({ brief, fieldKey }));
			const hasSeveralFields = part.fieldKeys.length > 1;
			const unlabelledFieldKeys = part.unlabelledFieldKeys || [];
			const lines = settledKeys.map(fieldKey => {
				const isNotNeeded = this.isBriefFieldNotNeeded({ brief, fieldKey });
				const text = isNotNeeded ? NOT_NEEDED_TEXT : brief[fieldKey];
				const isLabelShown = hasSeveralFields && !unlabelledFieldKeys.includes(fieldKey);
				const line = { label: fieldLabelByKey[fieldKey], text, isNotNeeded, isLabelShown };
				return line;
			});
			const isSomeFieldSettled = settledKeys.length > 0;
			const isEveryFieldSettled = settledKeys.length === part.fieldKeys.length;
			const isSkillsPart = part.key === "skills";
			let state = "waiting";
			if (isSomeFieldSettled) state = "partly";
			if (isEveryFieldSettled) state = "done";
			if (isSkillsPart) state = hasSkillsOrTools ? "done" : "waiting";
			const extras = extrasByPartKey[part.key] || [];
			const previewPart = { key: part.key, label: part.label, state, lines, extras, emptyText: part.emptyText };
			return previewPart;
		});

		const stages = this.describeBuildStages({ session });
		const testStage = stages.find(stage => stage.key === "test");
		let categoryValue = "Not set yet";
		if (category) categoryValue = category.isNew ? `${category.name} · new` : category.name;
		const draftValue = draft ? `Draft ${draft.versionNumber}` : "Not built yet";
		const isTestedOnAClient = testStage.state === "done" && !!testStage.detail;
		const testedValue = isTestedOnAClient ? testStage.detail : "Not yet";
		const details = [
			{ key: "category", label: BRIEF_CATEGORY_LABEL, value: categoryValue },
			{ key: "draft", label: "Draft", value: draftValue },
			{ key: "tested", label: "Tested on", value: testedValue },
		];

		const isEveryPartDone = parts.every(part => part.state === "done");
		const isHeadSettled = headPart.fieldKeys.every(fieldKey => this.isBriefFieldSettled({ brief, fieldKey }));
		const isFilling = !isEveryPartDone || !isHeadSettled || !category;
		return { name, description, parts, details, isFilling };
	},

	/**
	 * A playbook version's judgments in words: each decision point's question with the facts its
	 * findings record, and — when a test of the draft has run — how many of the test's findings had
	 * each required fact checked against its tool. A note says when it makes none (with the
	 * mapper's reason) or was never mapped.
	 *
	 * @param {Object} params
	 * @param {Object|null} params.decisionPoints { points, noJudgmentReason } or null
	 * @param {Object|null} [params.tracking] { coverage } from the draft's readiness, or null
	 * @return {Object} { judgments: [{ key, question, factsText, cannotCollect, coverageText }], note }
	 */
	describeDecisionPoints({ decisionPoints, tracking = null }) {
		if (!decisionPoints) return { judgments: [], note: "Not mapped yet — build again to map the decisions it makes" };
		const coverage = tracking && tracking.coverage ? tracking.coverage : null;
		const judgments = decisionPoints.points.map(point => ({
			key: point.key,
			question: point.question,
			factsText: this.describeTrackedFacts({ fields: point.trackedFields || [] }),
			cannotCollect: this.cannotCollectFacts({ decisionPoints, tracking, pointKey: point.key }),
			coverageText: coverage ? this.describeTrackedCoverage({ pointCoverage: coverage.find(item => item.pointKey === point.key) || null }) : null,
		}));
		const note = judgments.length === 0 ? `None — ${decisionPoints.noJudgmentReason}` : null;
		return { judgments, note };
	},

	/**
	 * A draft's statement lines in words: for each, the checks about it by label (saying so when
	 * they are all analytical — against last year, the usual side or a pattern, not a test of the
	 * balance), or the waiver a person recorded with who and when, or that no check is about it,
	 * which stops publishing until it is waived.
	 *
	 * @param {Object} params
	 * @param {Object|null} params.lineCoverage { period, lines } from the draft, or null
	 * @return {Object} { title, lines: [{ code, name, detail, isUnchecked, isWaived }] }
	 */
	describeDraftLines({ lineCoverage }) {
		const periodWords = { month_end: "month end", year_end: "year end" };
		const periodText = lineCoverage && periodWords[lineCoverage.period] ? ` at each ${periodWords[lineCoverage.period]}` : "";
		const title = `Statement lines checked${periodText}`;
		const coveredLines = lineCoverage ? lineCoverage.lines : [];
		const lines = coveredLines.map(line => {
			const checkLabels = line.checks.map(check => check.label).join("; ");
			const checksText = `${UtilityService.describeCount({ count: line.checks.length, singular: "check" })}: ${checkLabels}${line.isCheckedOnlyAnalytically ? " — analytical only, not a test of the balance" : ""}`;
			const waiverText = line.waiver
				? `Waived by ${line.waiver.waivedBy || "someone no longer listed"} on ${moment(line.waiver.waivedAt).format("MMM D, YYYY")}: ${line.waiver.reason}`
				: null;
			const isUnchecked = line.checks.length === 0;
			const uncheckedText = waiverText || "No check is about this line. Waive it with a reason, or tell the guide to leave it out.";
			const detail = isUnchecked ? uncheckedText : checksText;
			return { code: line.code, name: line.name, detail, isUnchecked: isUnchecked && !line.waiver, isWaived: !!line.waiver };
		});
		return { title, lines };
	},

	/**
	 * The facts a judgment's findings record, required ones first.
	 *
	 * @param {Object} params
	 * @param {Array} params.fields [{ label, required }]
	 * @return {String} empty when it records none
	 */
	describeTrackedFacts({ fields }) {
		const required = fields.filter(field => field.required).map(field => field.label);
		const optional = fields.filter(field => !field.required).map(field => field.label);
		if (required.length === 0 && optional.length === 0) return "";
		if (required.length === 0) return `Records ${optional.join(", ")}`;
		return `Records ${required.join(", ")}${optional.length > 0 ? `; also ${optional.join(", ")}` : ""}`;
	},

	/**
	 * How well the last test collected one judgment's required facts: how many of its findings had
	 * each one checked against the tool it came from, or that the test raised none under it.
	 *
	 * @param {Object} params
	 * @param {Object|null} params.pointCoverage { findings, fields: [{ label, required, verifiedIn }] }, null for a proposal
	 * @return {String|null}
	 */
	describeTrackedCoverage({ pointCoverage }) {
		if (!pointCoverage) return null;
		if (pointCoverage.findings === 0) return "Not exercised by the last test";
		const findingsText = UtilityService.describeCount({ count: pointCoverage.findings, singular: "finding" });
		const fieldTexts = pointCoverage.fields.filter(field => field.required).map(field => `${field.label} ${field.verifiedIn} of ${pointCoverage.findings}`);
		return `Last test: ${findingsText} — checked against the source: ${fieldTexts.join(", ")}`;
	},

	/**
	 * Whether a judgment cannot collect what it depends on: the build found a required fact whose
	 * tool the playbook lacks, or the last test raised findings under it and never checked a
	 * required fact in any of them.
	 *
	 * @param {Object} params
	 * @param {Object} params.decisionPoints { trackingCheck: { gaps: [{ pointKey }] } }
	 * @param {Object|null} params.tracking { uncollected: [{ pointKey }] }
	 * @param {String} params.pointKey
	 * @return {Boolean}
	 */
	cannotCollectFacts({ decisionPoints, tracking, pointKey }) {
		const hasBuildGap = !!decisionPoints.trackingCheck && decisionPoints.trackingCheck.gaps.some(gap => gap.pointKey === pointKey);
		const hasTestGap = !!tracking && tracking.uncollected.some(item => item.pointKey === pointKey);
		return hasBuildGap || hasTestGap;
	},
};

export default PlaybookBuildService;
